import { ReactNode, useMemo } from "react";
import { catcat as cc } from "cerulean";
import { useBreakpoints } from "@narmi/design_system";
import styles from "./Table.module.scss";

export type Breakpoint = "xs" | "s" | "m" | "l" | "xl";
export type BreakpointMap = Record<Exclude<Breakpoint, "xs">, boolean>;

export interface Column<T, K extends keyof T = keyof T> {
  header: string;
  accessor: K;
  render?: (
    cellValue: T[K],
    row: T,
    rowIndex: number,
    columnIndex: number
  ) => ReactNode;
  className?: string;
  hideAt?: Breakpoint[];
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  isHeaderVisible?: boolean;
  onRowClick?: (row: T) => void;
}

export function getCurrentBreakpoint({
  xl,
  l,
  m,
  s,
}: BreakpointMap): Breakpoint {
  switch (true) {
    case xl:
      return "xl";
    case l:
      return "l";
    case m:
      return "m";
    case s:
      return "s";
    default:
      return "xs";
  }
}

export function createHideMap<T>(
  columns: Column<T>[]
): Record<number, Record<Breakpoint, boolean>> {
  const bpMap: Record<number, Record<Breakpoint, boolean>> = {};

  columns.forEach((column, index) => {
    const entry: Record<Breakpoint, boolean> = {
      xs: false,
      s: false,
      m: false,
      l: false,
      xl: false,
    };
    column.hideAt?.forEach((bp) => {
      entry[bp] = true;
    });
    bpMap[index] = entry;
  });

  return bpMap;
}

function Table<T extends object>({
  columns,
  data,
  className,
  isHeaderVisible = true,
  onRowClick,
}: TableProps<T>): ReactNode {
  const { s, m, l, xl }: BreakpointMap = useBreakpoints();
  const hideMap = useMemo(() => createHideMap(columns), [columns]);
  const currentBreakpoint = useMemo(
    () => getCurrentBreakpoint({ s, m, l, xl }),
    [s, m, l, xl]
  );

  const visibleColumns = useMemo(
    () => columns.filter((_col, index) => !hideMap[index][currentBreakpoint]),
    [columns, hideMap, currentBreakpoint]
  );

  return (
    <div className={cc([styles.tableContainer, className])}>
      <table className={styles.table}>
        {isHeaderVisible && (
          <thead>
            <tr>
              {visibleColumns.map((column, columnIndex) => (
                <th
                  key={columnIndex}
                  className={cc([styles.headerCell, column.className])}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={cc({ [styles.clickableRow]: !!onRowClick })}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {visibleColumns.map((column, columnIndex) => {
                const cellValue = row[column.accessor];
                const cellContent = column.render
                  ? column.render(cellValue, row, rowIndex, columnIndex)
                  : (cellValue as unknown as ReactNode);

                return (
                  <td
                    key={columnIndex}
                    className={cc([styles.cell, column.className])}
                  >
                    {cellContent}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
